import * as Sentry from '@sentry/react-native';

/**
 * Structured, PHI-safe logger — the one sanctioned way to emit diagnostics
 * that survive in release builds (raw console.* is stripped from release by
 * babel-plugin-transform-remove-console).
 *
 * Rules (engineering-standards core/security + stacks/react-native):
 * - Pass explicit, allow-listed primitive fields — never whole objects,
 *   never glucose readings, identifiers, tokens, or profile data.
 *   Log who/what/when, never the PHI itself.
 *   BAD:  logger.error('sync failed', {reading, profile})
 *   GOOD: logger.error('sync failed', {count: batch.length, code: e.code})
 * - debug/info are dev-only. warn/error also reach Sentry in release (as
 *   breadcrumb / capture) so production failures are visible with context.
 */

// Drop anything that isn't a short primitive — a cheap backstop, not a
// substitute for passing safe fields in the first place.
const sanitizeFields = fields => {
  if (!fields || typeof fields !== 'object') {
    return undefined;
  }
  const safe = {};
  for (const [key, value] of Object.entries(fields)) {
    const type = typeof value;
    if (value === null || type === 'boolean' || type === 'number') {
      safe[key] = value;
    } else if (type === 'string') {
      safe[key] = value.length > 120 ? `${value.slice(0, 120)}…` : value;
    }
    // objects/arrays/functions are dropped: they smuggle PHI too easily
  }
  return safe;
};

const emit = (level, message, fields) => {
  const safeFields = sanitizeFields(fields);
  if (__DEV__) {
    // eslint-disable-next-line no-console
    console[level === 'debug' ? 'log' : level](
      `[${level}] ${message}`,
      safeFields ?? '',
    );
    return;
  }
  if (level === 'warn') {
    Sentry.addBreadcrumb({level: 'warning', message, data: safeFields});
  } else if (level === 'error') {
    Sentry.captureMessage(message, {level: 'error', extra: safeFields});
  }
  // debug/info are intentionally silent in release
};

export const logger = {
  debug: (message, fields) => emit('debug', message, fields),
  info: (message, fields) => emit('info', message, fields),
  warn: (message, fields) => emit('warn', message, fields),
  error: (message, fields) => emit('error', message, fields),
  /** Report a caught exception with safe context fields. */
  exception: (error, fields) => {
    if (__DEV__) {
      // eslint-disable-next-line no-console
      console.error(error, sanitizeFields(fields) ?? '');
      return;
    }
    Sentry.captureException(error, {extra: sanitizeFields(fields)});
  },
};

export default logger;
